import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import I18n from "common/i18n";
import Constants from "common/constant";
import { UserAvatar } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { AccountType } from "features/user/types";
import { ParentGate } from "features/parent/ParentGate";
import { ApiPieceComment } from "features/builder/types";
import { IconBad, IconFun, IconCool, IconAmazing, IconDifficult } from "features/reaction/asset";
import {
  getComments,
  getReplies,
  postComment,
  deleteComment,
  reactToComment,
  reportComment,
} from "features/builder/api";
import { InScreenModal } from "./ScreenModal";
import { SparkleBurst } from "./SparkleBurst";
import { BuilderReportDialog } from "./BuilderReportDialog";
import { BuilderConfirmDialog } from "./BuilderConfirmDialog";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";
import { BuilderAccountRequiredDialog } from "./BuilderAccountRequiredDialog";

const userIcon = (name: string) => `${Constants.assetHost}/assets/users/icons/${name}`;
const DEFAULT_USER_ICON = userIcon("icon_default_kage01-1.png");

enum ReactionKey {
  DIFFICULT = "difficult",
  EASY = "easy",
  COOL = "cool",
  FUN = "fun",
  AMAZING = "amazing",
  BAD = "bad",
}

// INFOタブ（MapDetailPage）と同じ本物のリアクションアイコン。絵文字は使わない。
const REACTIONS: { key: ReactionKey; icon: JSX.Element }[] = [
  { key: ReactionKey.DIFFICULT, icon: <IconDifficult width={28} height={28} /> },
  {
    key: ReactionKey.EASY,
    icon: (
      <img
        alt="*"
        draggable={false}
        className="w-7 h-7"
        src={`${Constants.assetHost}/assets/images/reaction_easy.png`}
      />
    ),
  },
  { key: ReactionKey.COOL, icon: <IconCool width={28} height={28} /> },
  { key: ReactionKey.FUN, icon: <IconFun width={28} height={28} /> },
  { key: ReactionKey.AMAZING, icon: <IconAmazing width={28} height={28} /> },
  { key: ReactionKey.BAD, icon: <IconBad width={28} height={28} /> },
];

type Message = {
  id: string;
  author: string;
  icon: string;
  content: string;
  min_ago: number;
  is_mine: boolean;
  liked: boolean;
  like_count: number;
  reactions: Record<ReactionKey, number>;
  myReactions: ReactionKey[];
};

type Comment = Message & { replies: Message[]; reply_count: number; repliesLoaded: boolean };

// API のコメント（ApiPieceComment）→ パネル内 Message へ変換
const toMessage = (c: ApiPieceComment): Message => ({
  id: c.id,
  author: c.author_name ?? "",
  icon: c.author_icon ?? DEFAULT_USER_ICON,
  content: c.content,
  min_ago: Math.max(0, Math.floor((Date.now() - new Date(c.created_at).getTime()) / 60000)),
  is_mine: c.is_author,
  liked: false,
  like_count: 0,
  reactions: {
    [ReactionKey.DIFFICULT]: c.reaction?.difficult ?? 0,
    [ReactionKey.EASY]: c.reaction?.easy ?? 0,
    [ReactionKey.COOL]: c.reaction?.cool ?? 0,
    [ReactionKey.FUN]: c.reaction?.fun ?? 0,
    [ReactionKey.AMAZING]: c.reaction?.amazing ?? 0,
    [ReactionKey.BAD]: c.reaction?.bad ?? 0,
  },
  myReactions: (c.my_reactions ?? []) as ReactionKey[],
});

const toComment = (c: ApiPieceComment): Comment => ({
  ...toMessage(c),
  replies: [],
  reply_count: c.reply_count ?? 0,
  repliesLoaded: false,
});

const relTime = (min: number): string => {
  if (min < 1) return I18n.t("MSG_BUILDER_DETAIL_TIME_MIN_AGO", { n: 0 });
  if (min < 60) return I18n.t("MSG_BUILDER_DETAIL_TIME_MIN_AGO", { n: min });
  if (min < 1440) return I18n.t("MSG_BUILDER_DETAIL_TIME_HOUR_AGO", { n: Math.floor(min / 60) });
  return I18n.t("MSG_BUILDER_DETAIL_TIME_DAY_AGO", { n: Math.floor(min / 1440) });
};

const toggledReaction = <
  T extends { reactions: Record<ReactionKey, number>; myReactions: ReactionKey[] },
>(
  m: T,
  key: ReactionKey,
): T => {
  const mine = m.myReactions.includes(key);
  return {
    ...m,
    reactions: { ...m.reactions, [key]: m.reactions[key] + (mine ? -1 : 1) },
    myReactions: mine ? m.myReactions.filter((k) => k !== key) : [...m.myReactions, key],
  };
};

// 本物のリアクションアイコン1個分のボタン（自然幅・引き伸ばさない）。
const ReactionButton = ({
  rkey,
  icon,
  count,
  mine,
  onToggle,
}: {
  rkey: ReactionKey;
  icon: JSX.Element;
  count: number;
  mine: boolean;
  onToggle: (key: ReactionKey) => void;
}) => (
  <button
    type="button"
    onClick={() => onToggle(rkey)}
    aria-label={`reaction-${rkey}`}
    className={`type-inherit-font flex-row-center gap-1 px-3 py-2 rounded-full border-none shadow-sm cursor-pointer ${
      mine ? "bg-blue-100/20" : "bg-white"
    }`}
  >
    {icon}
    <p className="type-text type-body text-textcolor-black/70">{count}</p>
  </button>
);

// メッセージ右上のモデレーション（通報 / 削除）。
const ModerationActions = ({
  isMine,
  onReport,
  onDelete,
}: {
  isMine: boolean;
  onReport: () => void;
  onDelete: () => void;
}) => (
  <div className="flex-row-el items-center shrink-0 gap-3">
    {/* 自分のコメントは通報できない（消すだけ） */}
    {!isMine && (
      <button
        type="button"
        onClick={onReport}
        aria-label={I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPORT")}
        className="type-inherit-font flex-row-center gap-1 px-3 py-2 rounded-full border-none shadow-sm bg-white cursor-pointer"
      >
        <span className="material-symbols-outlined type-md text-textcolor-black/40">flag</span>
        <p className="type-text type-sm text-textcolor-black/50 whitespace-nowrap">
          {I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPORT")}
        </p>
      </button>
    )}
    {isMine && (
      <button
        type="button"
        onClick={onDelete}
        aria-label={I18n.t("MSG_BUILDER_DETAIL_COMMENT_DELETE")}
        className="type-inherit-font flex-row-center gap-1 px-3 py-2 rounded-full border-none shadow-sm bg-white cursor-pointer"
      >
        <span className="material-symbols-outlined type-md text-danger">delete</span>
        <p className="type-text type-sm text-danger whitespace-nowrap">
          {I18n.t("MSG_BUILDER_DETAIL_COMMENT_DELETE")}
        </p>
      </button>
    )}
  </div>
);

// 返信ボタン。
const ReplyButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPLY")}
    className="type-inherit-font flex-row-center shrink-0 ml-auto gap-1 px-3 py-2 rounded-full border-none shadow-sm bg-white cursor-pointer"
  >
    <span className="material-symbols-outlined type-md text-blue-100">reply</span>
    <p className="type-text type-sm text-blue-100 whitespace-nowrap">
      {I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPLY")}
    </p>
  </button>
);

// メッセージ下部のアクション（リアクション2行 ＋ 2行目の右端に返信／同じ行）。
const MessageActions = ({
  reactions,
  myReactions,
  onReply,
  onToggleReaction,
}: {
  reactions: Record<ReactionKey, number>;
  myReactions: ReactionKey[];
  onReply?: () => void;
  onToggleReaction: (key: ReactionKey) => void;
}) => {
  const renderReaction = ({ key, icon }: { key: ReactionKey; icon: JSX.Element }) => (
    <ReactionButton
      key={key}
      rkey={key}
      icon={icon}
      count={reactions[key]}
      mine={myReactions.includes(key)}
      onToggle={onToggleReaction}
    />
  );

  return (
    <div className="flex-col-el w-full gap-2">
      {/* 1行目：リアクション3種 */}
      <div className="flex-row-el items-center w-full gap-2">
        {REACTIONS.slice(0, 3).map(renderReaction)}
      </div>

      {/* 2行目：リアクション3種 ＋ 同じ行の右端に返信 */}
      <div className="flex-row-el items-center w-full gap-2">
        {REACTIONS.slice(3).map(renderReaction)}
        {onReply && <ReplyButton onClick={onReply} />}
      </div>
    </div>
  );
};

// 返信入力 Modal（Twitter 風）。返信先メッセージ＋入力フォームをゲーム機画面内に表示。
const ReplyModal = ({
  target,
  onClose,
  onSubmit,
  validate,
}: {
  target: Message;
  onClose: () => void;
  onSubmit: (text: string) => void;
  /** 送る前の検査。文言のキーを返すと送らずにその理由を出す */
  validate?: (text: string) => string | null;
}) => {
  const playSend = usePlaySound(`${Constants.assetHost}/assets/blockly/start.mp3`);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState(false);

  const handleSend = () => {
    const content = text.trim();
    if (!content) return;
    // 送ってから断られても書き直せない。書いている場所で理由を出す
    const invalid = validate?.(content);
    if (invalid) {
      setError(invalid);
      return;
    }
    playSend();
    setCelebrate(true);
    btnDelay(() => onSubmit(content));
  };

  return (
    <InScreenModal open onBackdropClose={text.trim() ? undefined : onClose}>
      <BuilderModalPanel
        accent={BuilderModalAccent.BLUE}
        icon="reply"
        title={I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPLY")}
        width={480}
      >
        {celebrate && <SparkleBurst />}

        {/* 返信先メッセージ（引用） */}
        <div className="flex-row-el items-start w-full gap-2 rounded-xl p-3 bg-beige-60">
          <UserAvatar size={48} icon={target.icon} />
          <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
            <p className="type-text-left type-body text-textcolor-black w-full truncate">
              {target.author}
            </p>
            <p className="type-text-left type-body text-textcolor-black w-full">{target.content}</p>
          </div>
        </div>

        {error && (
          <div className="flex-row-el items-center w-full gap-2">
            <span className="flex-col-el flex-center material-symbols-outlined type-title text-danger">
              error
            </span>
            <p className="type-text-left type-body text-danger flex-1">{I18n.t(error)}</p>
          </div>
        )}

        {/* 返信入力 */}
        <textarea
          autoFocus
          className="type-text-left type-body text-textcolor-black w-full box-border rounded-xl p-3 border-solid border-2 border-gray2-30 outline-none resize-none bg-white focus:border-blue-100 focus:ring-4 focus:ring-blue-100/30"
          rows={3}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          placeholder={I18n.t("MSG_BUILDER_DETAIL_COMMENT_INPUT_PLACEHOLDER")}
        />

        <div className="flex-row-el flex-center w-full gap-2 upsilon">
          <button type="button" onClick={onClose} className="type-inherit-font btn btn-blue">
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-md">close</span>
              <p className="type-text type-body text-white">{I18n.t("MSG_STOP_BTN")}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!text.trim()}
            className="type-inherit-font btn btn-blue"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-md">send</span>
              <p className="type-text type-body text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_DETAIL_COMMENT_POST")}
              </p>
            </div>
          </button>
        </div>
      </BuilderModalPanel>
    </InScreenModal>
  );
};

// 送信に失敗した理由。手元の検査をすり抜けた場合の保険
const postErrorKey = (error: unknown) => {
  if (!axios.isAxiosError(error)) return "MSG_BUILDER_ACTION_FAILED";
  if (error.response?.status === 403) return "MSG_BUILDER_COMMENT_DENIED";
  if (error.response?.status === 400) return "MSG_BUILDER_COMMENT_BANNED_WORD";
  return "MSG_BUILDER_ACTION_FAILED";
};

export const MapDetailCommentPanel = ({
  pieceId,
  isExpanded = false,
  piecePublished = true,
  pieceCommentEnabled = true,
}: {
  pieceId: string;
  isExpanded?: boolean;
  /** 公開済みか。未公開のピースには誰もコメントできない */
  piecePublished?: boolean;
  /** 作者がこのピースのコメントを許可しているか。false なら API が 403 を返す */
  pieceCommentEnabled?: boolean;
}) => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const activeUser = useSelector((state: RootState) => state.user.active);
  const myIcon = activeUser?.icon ?? DEFAULT_USER_ICON;
  const uid = activeUser?.uid;
  // 保護者設定。OFF のときは投稿できないので入力欄と返信ボタンを出さない（閲覧は可）
  const commentEnabled = useSelector((state: RootState) => state.config.userConfig.comment_enabled);
  // 禁止ワードは名前の入力と同じく、手元の一覧で先に弾く
  const bannedWords = useSelector((state: RootState) => state.config.banned.words);
  const findBannedWord = (text: string) =>
    bannedWords.some((banned) => text.includes(banned.word))
      ? "MSG_BUILDER_COMMENT_BANNED_WORD"
      : null;
  // ゲストは API が 403 を返す。入力させてから断るのは不親切なので、先に理由を出す
  const isGuest = activeUser?.account_type === AccountType.GUEST;
  const canPost = commentEnabled && !isGuest && piecePublished && pieceCommentEnabled;
  const blockedReasonKey = isGuest
    ? "MSG_BUILDER_COMMENT_GUEST"
    : !commentEnabled
      ? "MSG_BUILDER_COMMENT_BLOCKED"
      : !piecePublished
        ? "MSG_BUILDER_COMMENT_UNPUBLISHED"
        : "MSG_BUILDER_COMMENT_OFF";

  const [comments, setComments] = useState<Comment[]>([]);
  const [openReplies, setOpenReplies] = useState<Record<string, boolean>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [replyTarget, setReplyTarget] = useState<{ commentId: string; message: Message } | null>(
    null,
  );
  const [postError, setPostError] = useState<string | null>(null);

  const [gateVisible, setGateVisible] = useState(false);
  const [showReportDenied, setShowReportDenied] = useState(false);

  // ゲストのままでは書けない。子どもに「とうろく」とは言わず、
  // アプリの他の場所と同じく「みまもりせってい」（おとなの確認 → 保護者登録）へ送る
  const openParentGate = () => {
    play();
    btnDelay(() => setGateVisible(true));
  };

  const handleGateConfirm = () => {
    setGateVisible(false);
    handleDialog({
      type: DialogType.PARENT_REGISTRATION,
      value: true,
      args: { redirectAfterSuccess: "/parent" },
    });
  };
  const [reportTarget, setReportTarget] = useState<{ commentId: string; content: string } | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<{ commentId: string; replyId?: string } | null>(
    null,
  );

  // トップコメント読み込み（新しい順）
  const load = useCallback(async () => {
    if (!pieceId) return;
    try {
      const items = await getComments({ piece_id: pieceId, uid });
      setComments(items.map(toComment));
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LOAD_FAILED")}</p>
        </div>,
      );
    }
  }, [pieceId, uid]);

  useEffect(() => {
    load();
  }, [load]);

  const updateComment = (id: string, fn: (c: Comment) => Comment) =>
    setComments((prev) => prev.map((c) => (c.id === id ? fn(c) : c)));

  const updateReply = (commentId: string, replyId: string, fn: (r: Message) => Message) =>
    updateComment(commentId, (c) => ({
      ...c,
      replies: c.replies.map((r) => (r.id === replyId ? fn(r) : r)),
    }));

  const handlePostComment = () => {
    const content = draft.trim();
    if (!content || !uid) return;
    play();
    const invalid = findBannedWord(content);
    if (invalid) {
      // 書いたものは消さない。直してもう一度 送れるようにする
      setPostError(invalid);
      return;
    }
    btnDelay(async () => {
      try {
        setPostError(null);
        await postComment({ piece_id: pieceId, uid, content });
        setDraft("");
        setComposerOpen(false);
        await load();
      } catch (error) {
        // 画面いちばん上のトーストでは気づけないので、入力欄のすぐ上に理由を出す
        setPostError(postErrorKey(error));
      }
    });
  };

  const cancelComposer = () => {
    play();
    setDraft("");
    setComposerOpen(false);
  };

  const toggleReplies = (id: string) => () => {
    play();
    btnDelay(async () => {
      const willOpen = !openReplies[id];
      const target = comments.find((c) => c.id === id);
      if (willOpen && target && !target.repliesLoaded) {
        try {
          const replies = await getReplies({ piece_id: pieceId, comment_id: id, uid });
          updateComment(id, (c) => ({
            ...c,
            replies: replies.map(toMessage),
            repliesLoaded: true,
          }));
        } catch {
          toast.warn(
            <div>
              <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LOAD_FAILED")}</p>
            </div>,
          );
          return;
        }
      }
      setOpenReplies((prev) => ({ ...prev, [id]: willOpen }));
    });
  };

  // リアクションは即時にローカル反映しつつサーバーへトグル送信
  const reactComment = (id: string) => (key: ReactionKey) => {
    if (!uid) return;
    play();
    updateComment(id, (c) => toggledReaction(c, key));
    reactToComment({ uid, comment_id: id, reaction: key }).catch(() => load());
  };

  const reactReply = (commentId: string, replyId: string) => (key: ReactionKey) => {
    if (!uid) return;
    play();
    updateReply(commentId, replyId, (r) => toggledReaction(r, key));
    reactToComment({ uid, comment_id: replyId, reaction: key }).catch(() => undefined);
  };

  const handleConfirmReport = (reason: string, detail: string) => {
    const target = reportTarget;
    setReportTarget(null);
    if (!target || !uid) return;
    reportComment({ piece_id: pieceId, comment_id: target.commentId, uid, reason, detail })
      .then(() =>
        toast.success(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_REPORT_THANKS")}</p>
          </div>,
        ),
      )
      .catch(() =>
        toast.warn(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
          </div>,
        ),
      );
  };

  const handleConfirmDelete = () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target || !uid) return;
    const { commentId, replyId } = target;
    const targetId = replyId ?? commentId;
    play();
    btnDelay(async () => {
      try {
        await deleteComment({ piece_id: pieceId, comment_id: targetId, uid });
        if (replyId) {
          updateComment(commentId, (c) => ({
            ...c,
            replies: c.replies.filter((r) => r.id !== replyId),
            reply_count: Math.max(0, c.reply_count - 1),
          }));
        } else {
          setComments((prev) => prev.filter((c) => c.id !== commentId));
        }
      } catch {
        toast.warn(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
          </div>,
        );
      }
    });
  };

  const openReply = (commentId: string, message: Message) => () => {
    play();
    setReplyTarget({
      commentId,
      message: { ...message, icon: message.is_mine ? myIcon : message.icon },
    });
  };

  const submitReply = (text: string) => {
    const target = replyTarget;
    if (!target || !uid) return;
    postComment({ piece_id: pieceId, uid, content: text, parent_comment_id: target.commentId })
      .then(async () => {
        const replies = await getReplies({
          piece_id: pieceId,
          comment_id: target.commentId,
          uid,
        });
        updateComment(target.commentId, (c) => ({
          ...c,
          replies: replies.map(toMessage),
          repliesLoaded: true,
          reply_count: replies.length,
        }));
        setOpenReplies((prev) => ({ ...prev, [target.commentId]: true }));
      })
      .catch((error) => setPostError(postErrorKey(error)));
    setReplyTarget(null);
  };

  return (
    <div className={`flex-col-el w-full gap-6 ${isExpanded ? "max-w-none" : "max-w-2xl mx-auto"}`}>
      {/* 投稿できない理由。黙って入力欄を消すと、なぜ書けないのか分からない。
          ゲストは登録すれば書けるようになるので、そのまま進めるボタンも出す */}
      {!canPost && (
        <div className="flex-row-el items-center w-full gap-2 p-3 rounded-2xl bg-white shadow-sm box-border">
          <span className="flex-col-el flex-center material-symbols-outlined type-title text-gray-100">
            info
          </span>
          <p className="type-text-left type-body text-textcolor-black flex-1">
            {I18n.t(blockedReasonKey)}
          </p>
          {isGuest && (
            <div className="upsilon shrink-0">
              <button
                type="button"
                onClick={openParentGate}
                className="type-inherit-font btn btn-primary"
              >
                <div className="flex-row-center gap-1">
                  <span className="material-symbols-outlined text-white type-md">
                    notification_important
                  </span>
                  <p className="type-text type-body text-white whitespace-nowrap">
                    {I18n.t("MSG_PARENT_REGISTRATION_PROMPT_CTA")}
                  </p>
                </div>
              </button>
            </div>
          )}
        </div>
      )}

      {/* 送信に失敗した理由（トーストは画面の上に出て見てもらえない） */}
      {postError && (
        <div className="flex-row-el items-center w-full gap-2 p-3 rounded-2xl bg-white shadow-sm box-border">
          <span className="flex-col-el flex-center material-symbols-outlined type-title text-danger">
            error
          </span>
          <p className="type-text-left type-body text-danger flex-1">{I18n.t(postError)}</p>
        </div>
      )}

      {/* 入力（上部・最初はコンパクト、タップで展開）。保護者が投稿を許可した場合のみ */}
      {canPost &&
        (composerOpen ? (
          <div className="flex-col-el w-full gap-2 rounded-2xl bg-white shadow-sm p-3">
            <textarea
              autoFocus
              className="type-text-left type-body text-textcolor-black w-full box-border rounded-xl p-3 border-solid border-2 border-gray2-30 outline-none resize-none"
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={I18n.t("MSG_BUILDER_DETAIL_COMMENT_INPUT_PLACEHOLDER")}
            />
            <div className="flex-row-el items-center justify-end w-full gap-2 upsilon">
              <button
                type="button"
                onClick={cancelComposer}
                className="type-inherit-font btn btn-blue"
              >
                <div className="flex-row-center gap-1">
                  <span className="material-symbols-outlined text-white type-md">close</span>
                  <p className="type-text type-body text-white whitespace-nowrap">
                    {I18n.t("MSG_STOP_BTN")}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={handlePostComment}
                disabled={!draft.trim()}
                className="type-inherit-font btn btn-primary disabled:opacity-50"
              >
                <div className="flex-row-center gap-1">
                  <span className="material-symbols-outlined text-white type-md">send</span>
                  <p className="type-text type-body text-white whitespace-nowrap">
                    {I18n.t("MSG_BUILDER_DETAIL_COMMENT_POST")}
                  </p>
                </div>
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              play();
              setComposerOpen(true);
            }}
            aria-label="write-comment"
            className="type-inherit-font flex-row-el items-center w-full gap-2 p-3 rounded-full border-none shadow-sm bg-white cursor-pointer"
          >
            <UserAvatar size={40} icon={myIcon} />
            <p className="type-text-left type-body text-textcolor-black/40">
              {I18n.t("MSG_BUILDER_DETAIL_COMMENT_INPUT_PLACEHOLDER")}
            </p>
          </button>
        ))}

      {/* コメント一覧（既存パネルがスクロール） */}
      {comments.map((c) => {
        const expanded = !!openReplies[c.id];
        return (
          <div
            key={c.id}
            className="flex-col-el w-full gap-3 rounded-2xl bg-beige-60 shadow-sm p-4"
          >
            {/* ヘッダー（右上に通報・削除） */}
            <div className="flex-row-el items-center w-full gap-2">
              <UserAvatar size={56} icon={c.is_mine ? myIcon : c.icon} />
              <div className="flex-col-el items-start flex-1 min-w-0">
                <p className="type-text-left type-body text-textcolor-black w-full truncate">
                  {c.author}
                </p>
                <p className="type-text-left type-sm text-textcolor-black/50">
                  {relTime(c.min_ago)}
                </p>
              </div>
              <ModerationActions
                isMine={c.is_mine}
                onReport={() =>
                  isGuest
                    ? setShowReportDenied(true)
                    : setReportTarget({ commentId: c.id, content: c.content })
                }
                onDelete={() => setDeleteTarget({ commentId: c.id })}
              />
            </div>

            {/* 本文 */}
            <p className="type-text-left type-body text-textcolor-black w-full">{c.content}</p>

            {/* アクション（リアクション2列 ＋ 右下に返信） */}
            <MessageActions
              reactions={c.reactions}
              myReactions={c.myReactions}
              onReply={canPost ? openReply(c.id, c) : undefined}
              onToggleReaction={reactComment(c.id)}
            />

            {/* 返信スレッド */}
            {c.reply_count > 0 && (
              <>
                <button
                  type="button"
                  onClick={toggleReplies(c.id)}
                  aria-label="toggle-replies"
                  className="type-inherit-font flex-row-el items-center self-start gap-1 border-none bg-transparent cursor-pointer"
                >
                  <span className="material-symbols-outlined type-md text-textcolor-black/50">
                    {expanded ? "expand_less" : "expand_more"}
                  </span>
                  <p className="type-text type-sm text-textcolor-black/60 whitespace-nowrap">
                    {expanded
                      ? I18n.t("MSG_BUILDER_DETAIL_COMMENT_HIDE_REPLIES")
                      : I18n.t("MSG_BUILDER_DETAIL_COMMENT_SHOW_REPLIES", { n: c.reply_count })}
                  </p>
                </button>

                {expanded && (
                  <div className="flex-col-el w-full gap-8">
                    {c.replies.map((r) => (
                      <div
                        key={r.id}
                        className="flex-col-el w-full gap-3 rounded-xl bg-white shadow-sm p-4"
                      >
                        <div className="flex-row-el items-center w-full gap-2">
                          <UserAvatar size={56} icon={r.is_mine ? myIcon : r.icon} />
                          <div className="flex-col-el items-start flex-1 min-w-0">
                            <p className="type-text-left type-body text-textcolor-black w-full truncate">
                              {r.author}
                            </p>
                            <p className="type-text-left type-sm text-textcolor-black/50">
                              {relTime(r.min_ago)}
                            </p>
                          </div>
                          <ModerationActions
                            isMine={r.is_mine}
                            onReport={() =>
                              isGuest
                                ? setShowReportDenied(true)
                                : setReportTarget({ commentId: r.id, content: r.content })
                            }
                            onDelete={() => setDeleteTarget({ commentId: c.id, replyId: r.id })}
                          />
                        </div>
                        <p className="type-text-left type-body text-textcolor-black w-full">
                          {r.content}
                        </p>
                        <MessageActions
                          reactions={r.reactions}
                          myReactions={r.myReactions}
                          onToggleReaction={reactReply(c.id, r.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {replyTarget && (
        <ReplyModal
          target={replyTarget.message}
          onClose={() => setReplyTarget(null)}
          onSubmit={submitReply}
          validate={findBannedWord}
        />
      )}

      {/* コメント通報（確認→お礼） */}
      <BuilderReportDialog
        visible={reportTarget != null}
        title={I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPORT")}
        target={reportTarget?.content ?? ""}
        message={I18n.t("MSG_BUILDER_DETAIL_COMMENT_REPORT_CONFIRM")}
        close={() => setReportTarget(null)}
        handleConfirm={handleConfirmReport}
      />

      {/* コメント削除（確認） */}
      <BuilderConfirmDialog
        visible={deleteTarget != null}
        icon="delete"
        confirmIcon="delete"
        confirmLabel={I18n.t("MSG_BUILDER_DETAIL_COMMENT_DELETE")}
        title={I18n.t("MSG_BUILDER_DETAIL_COMMENT_DELETE")}
        message={I18n.t("MSG_BUILDER_DETAIL_COMMENT_DELETE_CONFIRM")}
        close={() => setDeleteTarget(null)}
        handleConfirm={handleConfirmDelete}
      />

      {/* ゲストは通報できない。API を叩いてから断るのではなく、押した時点で理由を出す */}
      <BuilderAccountRequiredDialog
        visible={showReportDenied}
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        message={I18n.t("MSG_BUILDER_REPORT_NEED_ACCOUNT")}
        close={() => setShowReportDenied(false)}
      />

      {/* おとなの確認。通ったら保護者登録（みまもりせってい）へ */}
      <ParentGate
        visible={gateVisible}
        confirm={handleGateConfirm}
        close={() => setGateVisible(false)}
      />
    </div>
  );
};
